import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AppLayoutToolbar from "@cloudscape-design/components/app-layout-toolbar";
import BreadcrumbGroup from "@cloudscape-design/components/breadcrumb-group";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Tabs from "@cloudscape-design/components/tabs";
import Box from "@cloudscape-design/components/box";
import Header from "@cloudscape-design/components/header";
import Container from "@cloudscape-design/components/container";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Select from "@cloudscape-design/components/select";
import Button from "@cloudscape-design/components/button";
import Toggle from "@cloudscape-design/components/toggle";
import Table from "@cloudscape-design/components/table";
import Alert from "@cloudscape-design/components/alert";
import Modal from "@cloudscape-design/components/modal";
import CommonNavigation from "./CommonNavigation";
import Dashboard from "./Dashboard";
import DocumentUpload from "./DocumentUpload";
import DocumentList from "./DocumentList";
import DocumentTypeManager from "./DocumentTypeManager";
import CustomProcessingInstructions from "./CustomProcessingInstructions";
import WelcomeModal from "./WelcomeModal";
import AppHelpPanel from "./HelpPanel";
import CONFIG from "../config";

function VenueDocInterface({
  additionalNavItems = [],
  onLogout,
  currentView = "dashboard",
  onNavigate,
  isAdmin = false,
  currentInterface = "documents",
  idToken,
  currentUser,
}) {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeView, setActiveView] = useState("dashboard");
  const [docTabId, setDocTabId] = useState("upload");
  const [configTabId, setConfigTabId] = useState("doc-types");
  const [toolsOpen, setToolsOpen] = useState(false);
  const [autoOpenDocumentId, setAutoOpenDocumentId] = useState(null);
  const { t } = useTranslation();

  // --- Admin: Allowlist state ---
  const [whitelist, setWhitelist] = useState([]);
  const [entryType, setEntryType] = useState({ label: t("admin.allowlist.email") || "Email", value: "email" });
  const [entryValue, setEntryValue] = useState("");
  const [entryExpiry, setEntryExpiry] = useState("");
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminAlert, setAdminAlert] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ visible: false, entryId: null });

  // --- Admin: App Config state ---
  const [defaultDarkMode, setDefaultDarkMode] = useState({ label: "Light", value: "light" });
  const [showAllDocumentTabs, setShowAllDocumentTabs] = useState(false);

  const showAdminAlert = (message, type = "info") => {
    setAdminAlert({ message, type });
    setTimeout(() => setAdminAlert(null), 5000);
  };

  const loadWhitelist = useCallback(async () => {
    try {
      const response = await fetch(`${CONFIG.apiUrl}admin/whitelist`, {
        headers: { Authorization: idToken },
      });
      if (response.ok) {
        const data = await response.json();
        setWhitelist(data.entries || []);
      }
    } catch (error) {
      console.error("Failed to load whitelist:", error);
    }
  }, [idToken]);

  const loadAdminConfig = useCallback(async () => {
    try {
      const response = await fetch(`${CONFIG.apiUrl}admin/config`, {
        headers: { Authorization: idToken },
      });
      if (response.ok) {
        const data = await response.json();
        setDefaultDarkMode({
          label: data.default_dark_mode === "dark" ? "Dark" : "Light",
          value: data.default_dark_mode || "light",
        });
        setShowAllDocumentTabs(data.show_all_document_tabs || false);
      }
    } catch (error) {
      console.error("Failed to load admin config:", error);
    }
  }, [idToken]);

  // Load admin data when switching to config tab (admin-only tabs)
  // Also load config on mount to know whether to show bulk/other tabs
  useEffect(() => {
    loadAdminConfig();
  }, [loadAdminConfig]);

  useEffect(() => {
    if (activeView === "configuration" && isAdmin) {
      loadWhitelist();
    }
  }, [activeView, isAdmin, loadWhitelist]);

  const updateConfig = async (e) => {
    e.preventDefault();
    setAdminLoading(true);
    try {
      const response = await fetch(`${CONFIG.apiUrl}admin/config`, {
        method: "PUT",
        headers: { Authorization: idToken, "Content-Type": "application/json" },
        body: JSON.stringify({ default_dark_mode: defaultDarkMode.value, show_all_document_tabs: showAllDocumentTabs }),
      });
      if (response.ok) {
        showAdminAlert(t("messages.admin.configUpdated"), "success");
      } else {
        const data = await response.json();
        showAdminAlert(data.error || t("errors.admin.updateFailed"), "error");
      }
    } catch (error) {
      showAdminAlert(error.message, "error");
    } finally {
      setAdminLoading(false);
    }
  };

  const addWhitelistEntry = async (e) => {
    e.preventDefault();
    setAdminLoading(true);
    if (!entryValue) {
      showAdminAlert(t("errors.admin.enterEmail"), "error");
      setAdminLoading(false);
      return;
    }
    const body = { type: entryType.value, value: entryValue };
    if (entryExpiry) body.expiry_date = new Date(entryExpiry).toISOString();
    try {
      const response = await fetch(`${CONFIG.apiUrl}admin/whitelist`, {
        method: "POST",
        headers: { Authorization: idToken, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        showAdminAlert(t("messages.admin.entryAdded"), "success");
        setEntryValue("");
        setEntryExpiry("");
        loadWhitelist();
      } else {
        const data = await response.json();
        showAdminAlert(data.error || t("errors.admin.addFailed"), "error");
      }
    } catch (error) {
      showAdminAlert(error.message, "error");
    } finally {
      setAdminLoading(false);
    }
  };

  const deleteWhitelistEntry = async () => {
    setAdminLoading(true);
    try {
      const response = await fetch(`${CONFIG.apiUrl}admin/whitelist`, {
        method: "DELETE",
        headers: { Authorization: idToken, "Content-Type": "application/json" },
        body: JSON.stringify({ id: deleteModal.entryId }),
      });
      if (response.ok) {
        showAdminAlert(t("messages.admin.entryDeleted"), "success");
        loadWhitelist();
      } else {
        const data = await response.json();
        showAdminAlert(data.error || t("errors.admin.deleteFailed"), "error");
      }
    } catch (error) {
      showAdminAlert(error.message, "error");
    } finally {
      setAdminLoading(false);
      setDeleteModal({ visible: false, entryId: null });
    }
  };

  const handleNavigationFollow = (event) => {
    event.preventDefault();
    const href = event.detail.href;
    if (href === "#logout" && onLogout) {
      onLogout();
    } else if (href === "#/" || href === "#/dashboard") {
      setActiveView("dashboard");
    } else if (href === "#/documents") {
      setActiveView("documents");
    } else if (href === "#/configuration") {
      setActiveView("configuration");
    }
  };

  const handleUploadSuccess = (uploadedDocs) => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleDocumentProcessed = (docId) => {
    setRefreshTrigger((prev) => prev + 1);
    setDocTabId("my-uploads");
    setAutoOpenDocumentId(docId);
  };

  const getBreadcrumbs = () => {
    const items = [{ href: "#/", text: t("nav.menu.home") }];
    if (activeView === "dashboard") {
      items.push({ href: "#/dashboard", text: t("nav.menu.dashboard") });
    } else if (activeView === "documents") {
      items.push({ href: "#/documents", text: t("nav.menu.documentManagement") });
    } else if (activeView === "configuration") {
      items.push({ href: "#/configuration", text: t("nav.menu.configuration") });
    }
    return items;
  };

  const getHelpTabId = () => {
    if (activeView === "configuration") return "configuration";
    if (activeView === "documents") return "documents";
    return "dashboard";
  };

  const themeOptions = [
    { label: t("admin.config.light") || "Light", value: "light" },
    { label: t("admin.config.dark") || "Dark", value: "dark" },
  ];
  const entryTypeOptions = [
    { label: t("admin.allowlist.email") || "Email", value: "email" },
    { label: t("admin.allowlist.domain") || "Domain", value: "domain" },
  ];

  const renderDashboard = () => (
    <Dashboard
      idToken={idToken}
      onNavigate={(view) => {
        if (view === "documents") setActiveView("documents");
        else if (view === "document-types") {
          setActiveView("configuration");
          setConfigTabId("doc-types");
        }
      }}
      onLogout={onLogout}
    />
  );

  const renderDocumentManagement = () => {
    const docTabs = [
      {
        id: "upload",
        label: t("venue.documents.tabs.upload"),
        content: (
          <SpaceBetween size="l">
            <DocumentUpload idToken={idToken} onUploadSuccess={handleUploadSuccess} onLogout={onLogout} onNavigateToUploads={() => setDocTabId("my-uploads")} onDocumentProcessed={handleDocumentProcessed} />
          </SpaceBetween>
        ),
      },
      {
        id: "my-uploads",
        label: t("venue.documents.tabs.myUploads"),
        content: null,
      },
    ];

    // Show bulk/other tabs for admins or if config allows
    if (isAdmin || showAllDocumentTabs) {
      docTabs.push(
        { id: "bulk-uploads", label: t("venue.documents.tabs.bulkUploads"), content: null },
        { id: "other", label: t("venue.documents.tabs.other"), content: null },
      );
    }

    return (
      <SpaceBetween size="l">
        <Tabs
          activeTabId={docTabId}
          onChange={({ detail }) => setDocTabId(detail.activeTabId)}
          tabs={docTabs.map((tab) => ({
            ...tab,
            content:
              tab.id === "upload" ? (
                tab.content
              ) : tab.id === "bulk-uploads" ? (
                <SpaceBetween size="l">
                  <Alert type="info">
                    {t("venue.documents.bulk.description", "Documents uploaded directly to the S3 bucket are automatically processed and appear here.")}
                    {CONFIG.uploadBucketName && (
                      <>
                        {" "}
                        {t("venue.documents.bulk.bucketName", "Bucket:")}{" "}
                        <Box variant="code" display="inline">
                          {CONFIG.uploadBucketName}
                        </Box>
                      </>
                    )}
                  </Alert>
                  <DocumentList
                    idToken={idToken}
                    refreshTrigger={refreshTrigger}
                    onLogout={onLogout}
                    currentUser={currentUser}
                    isAdmin={isAdmin}
                    activeTabOverride={tab.id}
                  />
                </SpaceBetween>
              ) : tab.id === "other" ? (
                <SpaceBetween size="l">
                  <Alert type="info">
                    {t(
                      "venue.documents.other.description",
                      "Documents uploaded by other users. These are shared across the team for collaborative review and processing.",
                    )}
                  </Alert>
                  <DocumentList
                    idToken={idToken}
                    refreshTrigger={refreshTrigger}
                    onLogout={onLogout}
                    currentUser={currentUser}
                    isAdmin={isAdmin}
                    activeTabOverride={tab.id}
                  />
                </SpaceBetween>
              ) : (
                <DocumentList
                  idToken={idToken}
                  refreshTrigger={refreshTrigger}
                  onLogout={onLogout}
                  currentUser={currentUser}
                  isAdmin={isAdmin}
                  activeTabOverride={tab.id}
                  autoOpenDocumentId={tab.id === "my-uploads" ? autoOpenDocumentId : null}
                  onAutoOpenHandled={() => setAutoOpenDocumentId(null)}
                />
              ),
          }))}
        />
      </SpaceBetween>
    );
  };

  const renderConfiguration = () => {
    const configTabs = [
      {
        id: "doc-types",
        label: t("nav.menu.documentTypes"),
        content: (
          <SpaceBetween size="l">
            <Box variant="p" color="text-body-secondary">
              {t("venue.help.document-types.description")}
            </Box>
            <DocumentTypeManager idToken={idToken} onLogout={onLogout} />
          </SpaceBetween>
        ),
      },
      {
        id: "instructions",
        label: t("venue.config.instructions.title"),
        content: (
          <SpaceBetween size="l">
            <Box variant="p" color="text-body-secondary">
              {t("venue.config.instructions.description")}
            </Box>
            <CustomProcessingInstructions />
          </SpaceBetween>
        ),
      },
    ];

    if (isAdmin) {
      configTabs.push(
        {
          id: "allowlist",
          label: t("admin.allowlist.title"),
          content: (
            <SpaceBetween size="l">
              {adminAlert && (
                <Alert type={adminAlert.type} dismissible onDismiss={() => setAdminAlert(null)}>
                  {adminAlert.message}
                </Alert>
              )}
              <Container header={<Header variant="h2">{t("admin.allowlist.title")}</Header>}>
                <form onSubmit={addWhitelistEntry}>
                  <SpaceBetween size="l">
                    <FormField label={t("admin.allowlist.type")}>
                      <Select selectedOption={entryType} onChange={({ detail }) => setEntryType(detail.selectedOption)} options={entryTypeOptions} />
                    </FormField>
                    <FormField label={t("admin.allowlist.value")}>
                      <Input value={entryValue} onChange={({ detail }) => setEntryValue(detail.value)} placeholder={t("admin.allowlist.enterEmailOrDomain")} />
                    </FormField>
                    <FormField label={t("admin.allowlist.expiryDate")} description="Optional">
                      <Input value={entryExpiry} onChange={({ detail }) => setEntryExpiry(detail.value)} type="date" />
                    </FormField>
                    <Button variant="primary" loading={adminLoading} formAction="submit">
                      {t("admin.allowlist.addButton")}
                    </Button>
                  </SpaceBetween>
                </form>
              </Container>
              <Container header={<Header variant="h2">{t("admin.allowlist.currentTitle")}</Header>}>
                <Table
                  columnDefinitions={[
                    { id: "type", header: t("admin.allowlist.type"), cell: (item) => item.type },
                    { id: "value", header: t("admin.allowlist.value"), cell: (item) => item.value },
                    {
                      id: "expiry",
                      header: t("admin.allowlist.expiryDate"),
                      cell: (item) => (item.expiry_date !== "No expiry" && item.expiry_date.includes("T") ? item.expiry_date.split("T")[0] : item.expiry_date),
                    },
                    {
                      id: "action",
                      header: t("admin.allowlist.action"),
                      cell: (item) => (
                        <Button onClick={() => setDeleteModal({ visible: true, entryId: item.id })} variant="normal">
                          {t("admin.allowlist.deleteButton")}
                        </Button>
                      ),
                    },
                  ]}
                  items={whitelist}
                  empty={
                    <Box textAlign="center" color="inherit">
                      <b>{t("admin.allowlist.noEntries")}</b>
                    </Box>
                  }
                />
              </Container>
              <Modal
                visible={deleteModal.visible}
                onDismiss={() => setDeleteModal({ visible: false, entryId: null })}
                header={t("messages.admin.deleteEntry")}
                footer={
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button variant="link" onClick={() => setDeleteModal({ visible: false, entryId: null })}>
                        {t("venue.common.cancel")}
                      </Button>
                      <Button variant="primary" onClick={deleteWhitelistEntry} loading={adminLoading}>
                        {t("venue.common.delete")}
                      </Button>
                    </SpaceBetween>
                  </Box>
                }
              >
                {t("messages.admin.confirmDelete")}
              </Modal>
            </SpaceBetween>
          ),
        },
        {
          id: "app-config",
          label: t("admin.config.title"),
          content: (
            <SpaceBetween size="l">
              {adminAlert && (
                <Alert type={adminAlert.type} dismissible onDismiss={() => setAdminAlert(null)}>
                  {adminAlert.message}
                </Alert>
              )}
              <Container header={<Header variant="h2">{t("admin.config.title")}</Header>}>
                <form onSubmit={updateConfig}>
                  <SpaceBetween size="l">
                    <FormField label={t("admin.config.defaultTheme")}>
                      <Select selectedOption={defaultDarkMode} onChange={({ detail }) => setDefaultDarkMode(detail.selectedOption)} options={themeOptions} />
                    </FormField>
                    <FormField label={t("admin.config.showAllDocumentTabs")} description={t("admin.config.showAllDocumentTabsDescription")}>
                      <Toggle checked={showAllDocumentTabs} onChange={({ detail }) => setShowAllDocumentTabs(detail.checked)}>
                        {showAllDocumentTabs ? t("venue.common.enabled") : t("venue.common.disabled")}
                      </Toggle>
                    </FormField>
                    <Button variant="primary" loading={adminLoading} formAction="submit">
                      {t("admin.config.updateButton")}
                    </Button>
                  </SpaceBetween>
                </form>
              </Container>
            </SpaceBetween>
          ),
        },
      );
    }

    return <Tabs activeTabId={configTabId} onChange={({ detail }) => setConfigTabId(detail.activeTabId)} tabs={configTabs} />;
  };

  const renderContent = () => {
    switch (activeView) {
      case "documents":
        return renderDocumentManagement();
      case "configuration":
        return renderConfiguration();
      default:
        return renderDashboard();
    }
  };

  return (
    <>
      <WelcomeModal />
      <AppLayoutToolbar
        breadcrumbs={<BreadcrumbGroup items={getBreadcrumbs()} onFollow={handleNavigationFollow} />}
        tools={<AppHelpPanel activeTabId={getHelpTabId()} />}
        toolsOpen={toolsOpen}
        onToolsChange={({ detail }) => setToolsOpen(detail.open)}
        content={renderContent()}
        contentType="dashboard"
        navigation={
          <CommonNavigation
            activeHref={activeView === "dashboard" ? "#/dashboard" : activeView === "documents" ? "#/documents" : "#/configuration"}
            header={{ href: "#/", text: t("nav.menu.aiDocProcessing") }}
            onFollow={handleNavigationFollow}
            interfaceItems={[
              { href: "#/dashboard", text: t("nav.menu.dashboard"), type: "link" },
              { href: "#/documents", text: t("nav.menu.documentManagement"), type: "link" },
              { href: "#/configuration", text: t("nav.menu.configuration"), type: "link" },
            ]}
            isAdmin={false}
            additionalNavItems={additionalNavItems}
            currentInterface={currentInterface}
          />
        }
      />
    </>
  );
}

export default VenueDocInterface;
